'use client'

/**
 * The Save (heart) and Compare controls every product surface carries — FRIDGE-874.
 *
 * Before this, ONLY the homepage rails could write the saved list, so "Saved"
 * could never be populated by anyone actually shopping: no product page, no
 * collection, brand, filter, deals or article card had a heart at all. These two
 * controls are the one implementation those surfaces render.
 *
 * Deliberately LIGHT, for the same reason components/chrome/header-saved-compare.tsx
 * is: it imports the localStorage store and nothing else — no sprite, no image
 * component, no price formatter — so putting a heart on a collection card does not
 * pull the homepage package into that route's first-load JS (bundle-budget,
 * 09/03/2026). The two glyphs are the canvas sprite's `i-heart` and `i-check`
 * paths inline, because that sprite is mounted only on the homepage.
 *
 * Styling matches the homepage-2026 card anatomy (canvas.css `.phe` / `.pcompare`
 * / `.box`): a bare outlined heart top-right, red when saved, and a small square
 * box that fills black with a tick when the product is on the compare list.
 * Written in Tailwind rather than those classes because the classes are scoped to
 * `.hp26` and these cards are not inside it.
 *
 * Accessibility: the heart is a <button> with aria-pressed and a stateful label;
 * the compare control is a real <input type="checkbox"> inside its <label>, so
 * the whole label is the hit target. Both targets are at least 24x24 CSS px and
 * both paint a visible focus ring.
 */
import * as React from 'react'
import { useSavedCompare, COMPARE_MAX, type SavedItem } from '@/components/homepage-2026/saved-compare-store'
import { CHECK_SYMBOL_ID, HEART_FILLED_SYMBOL_ID, HEART_SYMBOL_ID } from '@/components/brand-kit/icon-symbol-ids'

export type { SavedItem }

const subscribeNever = () => () => {}
/**
 * false in the server HTML and on the first client render, true after
 * hydration — the same shape the store uses, so there is never a mismatch.
 *
 * Why the controls wait for hydration (09/06/2026): each card's Save button
 * and Compare label is ~1.2 KB of markup, and a 55-card brand page gained
 * 65 KB of server HTML — the html-render gate went red on four page types.
 * The controls are pure interaction: a crawler gains nothing from them and a
 * visitor cannot use them before hydration anyway. So the server sends a
 * same-height placeholder for the compare row and nothing for the heart
 * (it is absolutely positioned, so it takes no layout), and the real
 * controls mount on the client.
 */
function useHydrated(): boolean {
  return React.useSyncExternalStore(subscribeNever, () => true, () => false) || hydratedForTests
}
let hydratedForTests = false
/** Test seam: renderToStaticMarkup never hydrates, so the control suites flip this to render the mounted markup. */
export function __setHydratedForTests(value: boolean): void {
  hydratedForTests = value
}

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff9951] focus-visible:ring-offset-2 focus-visible:ring-offset-white'

/**
 * The Save heart, from the sitewide brand-kit sprite (app/layout.tsx mounts
 * <IconSprite /> on every page). One <use> per card instead of a full stroke
 * path per card — inline it cost /brands/lg 65 KB of server HTML (09/06/2026).
 * Two symbols carry the two states so no per-instance fill is needed.
 */
export function HeartGlyph({ filled, className = 'w-5 h-5' }: { filled?: boolean; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <use href={`#${filled ? HEART_FILLED_SYMBOL_ID : HEART_SYMBOL_ID}`} />
    </svg>
  )
}

/** The tick inside a ticked compare box — the sprite's check symbol. */
function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden="true">
      <use href={`#${CHECK_SYMBOL_ID}`} />
    </svg>
  )
}

/**
 * The heart. Position it yourself — on a card, `absolute top-2 right-2 z-20`
 * above the card's stretched link overlay; in a buy box, inline.
 */
export function SaveHeartButton({ item, className = '' }: { item: SavedItem; className?: string }) {
  const hydrated = useHydrated()
  const sc = useSavedCompare()
  const saved = sc.isSaved(item)
  if (!hydrated) return null
  return (
    <button
      type="button"
      data-saved-toggle
      onClick={(e) => {
        // On a card the whole surface is a stretched link to the product page.
        e.preventDefault()
        e.stopPropagation()
        sc.toggleSaved(item)
      }}
      aria-pressed={saved}
      aria-label={saved ? `Remove ${item.title} from saved` : `Save ${item.title}`}
      title={saved ? 'Remove from saved' : 'Save this product'}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-full bg-transparent transition-colors hover:bg-gray-100 ${
        saved ? 'text-[#dc2626]' : 'text-gray-600 hover:text-[#dc2626]'
      } ${FOCUS_RING} ${className}`}
    >
      <HeartGlyph filled={saved} />
    </button>
  )
}

/**
 * The compare tick. `full` widens the hit area to the row (the buy box variant);
 * the default is the compact card footer control.
 */
export function CompareToggle({
  item,
  className = '',
  labelText = 'Compare',
}: {
  item: SavedItem
  className?: string
  labelText?: string
}) {
  const sc = useSavedCompare()
  const compared = sc.isCompared(item)
  // At the cap the store does NOT refuse the tick — it drops the oldest row
  // (saved-compare-store.ts `toggleCompareItem`), which is why this control is
  // never disabled: a dead checkbox with no explanation is the worse failure.
  // The title says what will actually happen.
  const atCap = sc.compare.length >= COMPARE_MAX && !compared
  const id = React.useId()
  const hydrated = useHydrated()
  // The label is min-h-[24px] + py-1: 32px. The placeholder holds that height
  // so nothing moves when the control mounts.
  if (!hydrated) return <span data-compare-placeholder aria-hidden="true" className={`block min-h-[32px] ${className}`} />
  return (
    <label
      htmlFor={id}
      data-compare-toggle
      className={`inline-flex min-h-[24px] cursor-pointer select-none items-center gap-2 py-1 text-xs font-medium text-black ${className}`}
      title={
        atCap
          ? `Comparing ${COMPARE_MAX} already — adding this one drops the first`
          : undefined
      }
    >
      <input
        id={id}
        type="checkbox"
        checked={compared}
        onChange={() => sc.toggleCompare(item)}
        // Defensive only: no mount of this control sits inside a click-handling
        // ancestor today (verified on the grid card, the carousel card, the
        // deals card, the product client and the sticky bar), so this changes
        // nothing now — it stops a future card-level handler from swallowing
        // the tick. It lives on the interactive <input>, not the <label>, which
        // is what the accessibility ratchet requires (a label with a click
        // handler is a non-interactive element taking interactions).
        onClick={(e) => e.stopPropagation()}
        aria-label={`Compare ${item.title}`}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={`inline-flex h-4 w-4 flex-none items-center justify-center rounded border-[1.5px] transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#ff9951] peer-focus-visible:ring-offset-2 ${
          compared ? 'border-black bg-black' : 'border-black bg-white'
        }`}
      >
        {compared ? <CheckGlyph /> : null}
      </span>
      {labelText}
    </label>
  )
}

/** Heart + compare tick on one row — the product page buy box and sticky bars. */
export function SaveCompareRow({ item, className = '' }: { item: SavedItem; className?: string }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <SaveHeartButton item={item} className="-ml-1.5" />
      <CompareToggle item={item} labelText="Compare" className="text-[13px]" />
    </div>
  )
}
